import Mousetrap from 'mousetrap';
import { KeyboardEvent, RefObject } from 'react';
import type { RootStore } from '.';
import { isMac } from './utils';
import { QueueTabName } from './ui-active-session';

const IS_MAC = isMac();

interface KeyboardKey {
  key: string;
  symbol: string;
}

export interface KeyboardShortcut {
  name: string;
  combination: (KeyboardKey | string)[];
  fn: (e: Mousetrap.ExtendedKeyboardEvent) => void;
  description?: string;
}

export const CMD_KEY: KeyboardKey = { key: 'command', symbol: '⌘' };
export const CTRL_KEY: KeyboardKey = { key: 'ctrl', symbol: '⌃' };
export const META_KEY = IS_MAC ? CMD_KEY : CTRL_KEY;

export function getShortcutStringWithKeys(shortcut: KeyboardShortcut) {
  return shortcut.combination.map((s) => (typeof s === 'string' ? s : s.key)).join('+');
}
function getShortcutStringWithSymbols(shortcut: KeyboardShortcut) {
  return shortcut.combination.map((s) => (typeof s === 'string' ? s : s.symbol)).join('+');
}
export function createMetaShortcut(key: string) {
  return `${IS_MAC ? CMD_KEY.symbol : CTRL_KEY.symbol}${key}`;
}

export function matchMetaShortcutToEvent(event: KeyboardEvent, key: string) {
  return ((IS_MAC && event.metaKey) || (!IS_MAC && event.ctrlKey)) && event.key === key;
}

export class UiKeyboardShortcutsStore {
  private rootStore: RootStore;
  shortcuts: KeyboardShortcut[];
  private sourceAutocompleteRef: RefObject<HTMLDivElement> | null = null;

  setSourceAutocompleteRef(ref: RefObject<HTMLDivElement>) {
    this.sourceAutocompleteRef = ref;
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.shortcuts = [
      // Removed single-letter shortcut 'n' to improve typing experience
      // {
      //   name: 'newCards',
      //   combination: ['n'],
      //   fn: () => {
      //     if (this.rootStore.uiPermissions.canEditCurrentSession) {
      //       this.rootStore.uiBatchCardsStore.activateBatchMode();
      //     }
      //   },
      // },
      {
        name: 'completeCurrent',
        combination: [META_KEY, 'shift', 'd'],
        fn: (e) => {
          e.preventDefault();
          const { completeCard } = this.rootStore.sessionCardActions;
          const { currentCardId } = this.rootStore.sessionsStore.currentSession!;
          if (this.rootStore.uiPermissions.canEditCurrentSession) {
            completeCard(currentCardId);
          }
        },
      },
      {
        name: 'copyCurrentFilename',
        combination: [META_KEY, 'shift', 'c'],
        fn: (e) => {
          // Prevents chrome from opening dev tools
          e.preventDefault();
          const { currentCardId } = this.rootStore.sessionsStore.currentSession!;
          const { filename, cardNumber } = this.rootStore.cardsStore.cards[currentCardId];
          navigator.clipboard.writeText(filename);
          this.rootStore.uiSnackbarStore.setSnackbarDetails({
            description: `Card ${cardNumber}'s Filename copied to Clipboard`,
          });
        },
      },
      {
        name: 'undo',
        combination: [META_KEY, 'z'],
        fn: () => this.rootStore.uiSnackbarStore.runSnackbarAction(),
      },
      {
        name: 'clearFilters',
        combination: [META_KEY, 'esc'],
        fn: () => {
          this.rootStore.uiCardSearchStore.setCurrentSearch(undefined);
          this.rootStore.uiModulePowerColumnStore.clearAllModuleFilters();
        },
      },
      {
        name: 'deselectAllOrClearFilters',
        combination: ['escape'],
        fn: () => {
          const { selectedCardIds } = this.rootStore.uiActiveSessionStore;
          const { hasSearch } = this.rootStore.uiCardSearchStore;
          const { hasAnyPowerColumnFilters } = this.rootStore.uiModulePowerColumnStore;

          // If cards selected, deselect them first
          if (selectedCardIds.length > 0) {
            this.rootStore.uiActiveSessionStore.deselectAllCards();
            return;
          }

          // If no cards selected but filters active, clear filters
          if (hasSearch || hasAnyPowerColumnFilters) {
            this.rootStore.uiCardSearchStore.setCurrentSearch(undefined);
            this.rootStore.uiModulePowerColumnStore.clearAllModuleFilters();
          }
        },
      },
      {
        name: 'completeAllSelected',
        combination: [META_KEY, 'e'],
        fn: async () => {
          this.rootStore.sessionCardActions.completeOrMoveSelectedCards();
        },
      },
      {
        name: 'selectAll',
        combination: [META_KEY, 'a'],
        fn: (e) => {
          const { selectAllOrBelow } = this.rootStore.uiActiveSessionStore;
          e.preventDefault();
          selectAllOrBelow();
        },
      },
      // Removed single-letter shortcut 'x' to improve typing experience
      // {
      //   name: 'toggleView',
      //   combination: ['x'],
      //   fn: () => {
      //     this.rootStore.uiActiveSessionStore.toggleCardView();
      //   },
      // },
      {
        name: 'scrollTop',
        combination: [META_KEY, 'up'],
        fn: () => {
          this.rootStore.uiQueueScroll.scrollToTop();
        },
      },
      {
        name: 'scrollEnd',
        combination: [META_KEY, 'down'],
        fn: () => {
          this.rootStore.uiQueueScroll.scrollToEnd();
        },
      },
      {
        name: 'openSourceDropdown',
        combination: [META_KEY, 'l'],
        fn: (e) => {
          e.preventDefault();
          const input = this.sourceAutocompleteRef?.current?.querySelector('input');
          if (input) {
            input.focus();
          }
        },
      },
      {
        name: 'focusQueueSearch',
        combination: [META_KEY, '/'],
        fn: (e) => {
          e.preventDefault();
          const queueSearchInput = document.querySelector('input[placeholder="Find Cards"]');
          if (queueSearchInput) {
            (queueSearchInput as HTMLInputElement).focus();
          }
        },
      },
      {
        name: 'presentQueue',
        combination: ['alt', 'p'],
        fn: () => {
          const { currentQueueTab } = this.rootStore.uiActiveSessionStore;
          const { currentSession } = this.rootStore.sessionsStore;
          const ids =
            currentQueueTab === QueueTabName.Done
              ? currentSession?.doneVisibleCardIds
              : currentSession?.inProgressVisibleCardIds;
          const firstCardId = ids?.length ? ids[0] : undefined;

          if (firstCardId) {
            this.rootStore.uiCardView.toggleCardFocus(firstCardId);
          }
        },
      },
      {
        name: 'focusLibrarySearch',
        combination: [IS_MAC ? 'alt' : 'alt', META_KEY, '/'],
        fn: (e) => {
          e.preventDefault();
          const librarySearchInput = document.querySelector('input[placeholder="Search"]');
          if (librarySearchInput) {
            (librarySearchInput as HTMLInputElement).focus();
          }
        },
      },
      {
        name: 'toggleInfoPanel',
        combination: [META_KEY, 'i'],
        fn: (e) => {
          e.preventDefault();
          this.rootStore.uiInfoPanelStore.toggle();
        },
        description: 'Toggle info panel',
      },
    ];
  }

  getShortcutCombinationByName = (name: string) => {
    const shortcut = this.shortcuts.find((s) => s.name === name);
    if (!shortcut) {
      return 'N/A';
    }
    return getShortcutStringWithSymbols(shortcut);
  };

  init() {
    this.shortcuts.forEach((shortcut) => {
      Mousetrap.bind(getShortcutStringWithKeys(shortcut), shortcut.fn);
    });
  }
}
